import * as _ from 'lodash';
import type { ReactElement, VoidFunctionComponent } from 'react';
import { useCallback } from 'react';
import { Button } from 'react-bootstrap';

import Text from '../../../generic/Text';
import { icons } from '../../../generic/Icon/icons';
import ItemizedRule from './ItemizedRule';
import ItemizedRuleListInput from './ItemizedRuleListInput';
import type { Suggestion } from './types';
import { Icon } from '../../../generic/Icon';

type ItemizedRuleListProps = {
  listTitle: string;
  listDescription: ReactElement;
  listItemInputDecoration: string;
  getSuggestions: (t: string) => Promise<Suggestion[]>;
  /**
   * Rules already selected for a policy
   */
  rules: Array<string | null>,
  /**
   * Sets the list of rules. If last element is null, it shows that we should allow editing
   * Otherwise, the user needs to trigger adding a null item to the end of the list.
   */
  setRuleList: (ids: Array<string | null>) => void;
  // addRule: (id: string | null) => void;
  // removeRule: (id: string) => void;
  getTitle: (id: string) => string;
  /**
   * Function that builds a link given an id. If this function is not provided, no link is used
   */
  buildLink?: (id: string) => string;
};

const ItemizedRuleList: VoidFunctionComponent<ItemizedRuleListProps> = ({
  listTitle,
  listDescription,
  listItemInputDecoration,
  getSuggestions,
  rules,
  setRuleList,
  // addRule,
  // removeRule,
  getTitle,
  buildLink,
}) => {
  const addRule = (id: string | null) => {
    const newRules = [...rules] as Array<string | null>;
    newRules.push(id);
    // this sort operation ensures that NULL is at the end of the list
    // to tell the UI to allow new entries AND retains the existing order
    // of the other entries in the cwe allow list
    setRuleList(_.uniq(newRules).sort((a, b) => {
      if (a === null) {
        return 1;
      }
      if (b === null) {
        return -1;
      }

      return 0;
    }));
  };

  const removeRule = (id: string | null) => {
    setRuleList(rules.filter((rule) => rule !== id));
  };
  const isRuleIsEditable = useCallback((ruleId: string | null): ruleId is null => {
    return ruleId === null ||
      !rules.includes(ruleId);
  }, [rules]);

  const identifierTitle = listItemInputDecoration.replace(/[^a-zA-Z]/, '');

  return (
    <div className="card">
      <div className="row">
        <div className="col-md-3">
          <Text.H3>{listTitle}</Text.H3>
          {listDescription}
        </div>
        <div className="col-md-9">
          <div className="card">
            <Text.H4>{identifierTitle} Identifiers</Text.H4>
            <div className={rules.length > 0 ? 'card' : ''}>
              {rules.map((rule) => (
                <div className="row margin-bottom-md" style={{ width: '525px' }}>
                  {isRuleIsEditable(rule) ?
                    <ItemizedRuleListInput
                      addRule={(id: string | null) => {
                        if (id) {
                          addRule(id);
                        }
                      }}
                      decoration={listItemInputDecoration}
                      removeRule={() => removeRule(rule)}
                      getSuggestions={getSuggestions}
                    />
                    :
                    <ItemizedRule
                      link={buildLink ? buildLink(rule) : undefined}
                      id={rule}
                      title={getTitle(rule)}
                      removeRule={() => removeRule(rule)}
                    />}
                </div>
              ))}
            </div>
            {/* Adding a null rule indicates that a new rule input is needed */}
            <Button onClick={() => addRule(null)}>
              <span><Icon icon={icons.plus} /></span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemizedRuleList;
